import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiCheckCircle } from "react-icons/hi";

import { CustomButton } from "../atoms/CustomButton";
import { ModalShell } from "../atoms/ModalShell";
import NoTranslate from "../atoms/NoTranslate";
import useAuth from "../../hooks/useAuth";
import {
  useEmailSenderValidationStatus,
  useRequestEmailSenderValidation,
  useConfirmEmailSenderValidation,
} from "../../hooks/entity";

type Props = {
  show: boolean;
  onClose: () => void;
  pendingEmail?: string | null;
};

const makeEmptyDigits = (): string[] => ["", "", "", "", "", ""];

const EmailSenderValidationModal: React.FC<Props> = ({
  show,
  onClose,
  pendingEmail,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const entity_identification = user?.entity_identification;

  const { data: status } = useEmailSenderValidationStatus(
    customer_identification,
    entity_identification,
    {
      enabled: !!customer_identification && !!entity_identification && show,
    }
  );

  const { mutate: requestCode, isLoading: isRequesting } =
    useRequestEmailSenderValidation();
  const { mutate: confirmCode, isLoading: isConfirming } =
    useConfirmEmailSenderValidation();

  const targetEmail = pendingEmail?.trim() || status?.email || "";

  const expiresAt = status?.code_expires_at
    ? new Date(status.code_expires_at).getTime()
    : 0;

  const isValidatedForTarget =
    !!status?.is_valid &&
    (!targetEmail ||
      (status?.email ?? "").toLowerCase() === targetEmail.toLowerCase());

  const [digits, setDigits] = useState<string[]>(makeEmptyDigits);
  const [timer, setTimer] = useState<number>(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmitTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (show) setDigits(makeEmptyDigits());
  }, [show]);

  useEffect(() => {
    if (!show || !expiresAt || expiresAt <= Date.now()) {
      setTimer(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000)
      );
      setTimer(remaining);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [show, expiresAt]);

  useEffect(() => {
    if (!show && autoSubmitTimeoutRef.current !== null) {
      window.clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    return () => {
      if (autoSubmitTimeoutRef.current !== null) {
        window.clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
    };
  }, [show]);

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleConfirm = (codeOverride?: string) => {
    const code = codeOverride ?? digits.join("");
    if (code.length !== 6 || !entity_identification) return;
    confirmCode(
      {
        customer_identification,
        entity_identification,
        code,
      },
      {
        onSuccess: () => {
          setDigits(makeEmptyDigits());
          onClose();
        },
      }
    );
  };

  const handleResend = () => {
    if (!entity_identification) return;
    setDigits(makeEmptyDigits());
    requestCode({
      customer_identification,
      entity_identification,
      email: pendingEmail || undefined,
    });
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const next = [...digits];
    next[index] = value;
    setDigits(next);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }

    if (value && index === 5 && next.every((d) => d !== "")) {
      autoSubmitTimeoutRef.current = window.setTimeout(() => {
        autoSubmitTimeoutRef.current = null;
        handleConfirm(next.join(""));
      }, 100);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const next = [...digits];
        next[index - 1] = "";
        setDigits(next);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text").trim().slice(0, 6);
    if (!/^\d+$/.test(pasted)) return;

    const next = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
    setDigits(next);

    const firstEmpty = next.findIndex((d) => d === "");
    inputsRef.current[firstEmpty === -1 ? 5 : firstEmpty]?.focus();

    if (pasted.length === 6) {
      autoSubmitTimeoutRef.current = window.setTimeout(() => {
        autoSubmitTimeoutRef.current = null;
        handleConfirm(pasted);
      }, 100);
    }
  };

  const allFilled = digits.every((d) => d !== "");
  const isExpired = expiresAt > 0 && expiresAt <= Date.now();

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      title={t("dashboard.entities.emailSenderValidation.title")}
      footer={
        isValidatedForTarget ? (
          <CustomButton
            variant="primary"
            onClick={onClose}
            title={t("common.close")}
          />
        ) : (
          <CustomButton
            variant="primary"
            onClick={() => handleConfirm()}
            disabled={!allFilled || isConfirming || isExpired}
            isLoading={isConfirming}
            title={
              isConfirming
                ? t("dashboard.entities.emailSenderValidation.verifying")
                : t("dashboard.entities.emailSenderValidation.verifyCode")
            }
          />
        )
      }
    >
      <div className="flex flex-col items-center gap-6 w-full min-w-0">
        {isValidatedForTarget ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <HiCheckCircle className="w-12 h-12 text-green-500" />
            <p className="text-center text-sm text-gray-700">
              {t("dashboard.entities.emailSenderValidation.alreadyValid")}
            </p>
          </div>
        ) : (
          <>
            <div className="w-full min-w-0 text-center">
              <p className="text-sm text-gray-500">
                {isRequesting
                  ? t("dashboard.entities.emailSenderValidation.sending")
                  : t("dashboard.entities.emailSenderValidation.sentTo")}
              </p>
              <p
                className="text-lg font-bold text-gray-900 mt-1 block w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                title={targetEmail || undefined}
              >
                {targetEmail || "—"}
              </p>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                {t("dashboard.entities.emailSenderValidation.description")}
              </p>
              {timer > 0 && (
                <div className="text-xs mt-3">
                  <span className="text-gray-500">
                    {t("dashboard.entities.emailSenderValidation.expiresIn")}{" "}
                    <span className="font-semibold text-blue-600">
                      {formatTimer(timer)}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <NoTranslate>
              <div className="flex gap-1">
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label={`${t(
                      "dashboard.entities.emailSenderValidation.codeLabel"
                    )} ${index + 1}`}
                    value={digit}
                    maxLength={1}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={isConfirming || isExpired}
                    className={`
                        w-10 h-14
                        text-center
                        text-lg
                        p-2
                        font-semibold
                        border-2
                        rounded-lg
                        transition-all
                        outline-none
                        ${
                          isExpired
                            ? "border-red-300 bg-red-50"
                            : digit
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-400 bg-white"
                        }
                        focus:border-blue-600
                        focus:shadow-sm
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                      `}
                  />
                ))}
              </div>
            </NoTranslate>

            {isExpired && expiresAt > 0 && (
              <div className="text-center">
                <p className="text-sm text-red-600 font-medium mb-3">
                  {t("dashboard.entities.emailSenderValidation.expired")}
                </p>
                <button
                  onClick={handleResend}
                  disabled={isRequesting}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("dashboard.entities.emailSenderValidation.sendNewCode")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
};

export default EmailSenderValidationModal;
